import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  Renderer2,
  ElementRef
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import {
  LEGAL_URLS,
  ProductType,
  PRODUCT_TYPES,
  SOLARTS_ROOFS_TYPES
} from '@apps/api-interfaces';
import { Destroyer, sleep } from '@apps/tools';
import {
  StorageService,
  zipCodeValidator,
  DetectDeviceService
} from '@apps/ui';
import {
  scaleAnimation,
  FormSubmitDirective,
  ErrorMessageComponent
} from '@apps/ui';
import animateScrollTo from 'animated-scroll-to';

const errorsZip = {
  required: () => `Kod pocztowy jest wymagany`,
  zipCode: () => `Błędny format kodu pocztowego`
};
const errorsSolars = {
  required: () => `Wybierz rodzaj powierzchni do montażu`
};
const errorsEnergy = {
  required: () => `Wybierz obecnego dostawcę`
};

const getQueryParam = (name: string): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export enum WidgetType {
  NORMAL,
  ONE_PER_PAGE
}

export enum FormSteps {
  ZIPCODE,
  CHOISE,
  AMOUNT,
  TERM
}

@Component({
  selector: 'sc-initial-form',
  templateUrl: './initial-form.component.html',
  styleUrls: ['./initial-form.component.scss'],
  animations: [scaleAnimation]
})
export class InitialFormComponent extends Destroyer implements OnInit {
  form: FormGroup;
  showMore: boolean;
  submitted: boolean;
  termFullView: boolean;
  choisePreview: string;
  errorsZip = errorsZip;
  errorsSolars = errorsSolars;
  errorsEnergy = errorsEnergy;
  roofs = SOLARTS_ROOFS_TYPES.filter(e => e.id !== 'other');
  currentView: FormSteps;
  viewTypes = FormSteps;
  rangeSelected: boolean;

  @Input() linkFilterFn = this.defaultLinkFilterFn;

  // Linki prawne - pelne URL-e na zatrzymajlicznik.pl, otwierane w nowym oknie.
  legal = LEGAL_URLS;
  @Input() disableTerms: boolean;
  @Input() small: boolean;
  @Input() buttonText: string;

  private _type: WidgetType = WidgetType.NORMAL;
  @Input() set type(type: WidgetType) {
    this._type = Number.isFinite(+type) ? +type : WidgetType.NORMAL;
  }
  get type(): WidgetType {
    return this._type;
  }

  private _zipCode: string = this.storage.getItem('zip') || null;
  @Input() set zipCode(zip: string) {
    this._zipCode = zip || this.storage.getItem('zip') || null;
  }
  get zipCode(): string {
    return this._zipCode;
  }

  private _product: ProductType = PRODUCT_TYPES[0].id as ProductType;
  @Input() set product(e: ProductType) {
    this._product = e;
    this.setFormValues();
  }
  get product(): ProductType {
    return this._product;
  }

  get hiddenElements(): string[] {
    const s = getQueryParam('hide') || '';
    return s.split(',').filter(e => e !== '');
  }

  get buttonTextMessage(): string {
    return getQueryParam('text') || this.buttonText || 'Porównaj ceny';
  }

  @Output() onsubmit: EventEmitter<any> = new EventEmitter();

  @ViewChild('formRef', { static: false, read: FormSubmitDirective })
  formRef: FormSubmitDirective;

  @ViewChildren('errorRef', { read: ErrorMessageComponent })
  errorRefs: QueryList<ErrorMessageComponent>;

  constructor(
    private fb: FormBuilder,
    private storage: StorageService,
    private cdr: ChangeDetectorRef,
    private device: DetectDeviceService,
    private renderer: Renderer2,
    private element: ElementRef
  ) {
    super();
  }

  ngOnInit() {
    this.setAssetExtension();
    this.rangeSelected = this.type === WidgetType.NORMAL;
    const termAccepted = !!this.storage.getItem('term');

    this.form = this.fb.group(
      {
        zip: [this.zipCode, [Validators.required, zipCodeValidator]],
        choise: [null, [Validators.required]],
        value: [60, [Validators.required]],
        term: [
          termAccepted,
          !this.disableTerms ? [Validators.requiredTrue] : []
        ]
      },
      {
        updateOn: 'submit'
      }
    );

    this.setCurrentState();
    this.setFormValues();
  }

  onInputChanged(value?: any) {
    this.submitted = false;
    try {
      this.resetErrors();
      this.form.markAsPristine();
    } catch (e) {}
  }

  resetErrors() {
    this.formRef.resetSubmit();
    this.errorRefs.toArray().forEach(err => {
      err.reset();
    });
  }

  defaultLinkFilterFn(url: string, event: Event) {
    event.preventDefault();
    this.onsubmit.emit({
      url
    });
  }

  submit() {
    this.submitted = true;
    if (!this.form.valid || !this.rangeSelected) {
      this.setCurrentState();
      this.cdr.detectChanges();
      this.scrollToError();
      return;
    }

    const values = this.form.value;

    switch (this.product) {
      case 'prad':
      case 'gaz':
        values['provider'] = values['choise'];
        this.storage.setItem('provider', values['choise']);
        break;
      case 'fotowoltaika':
        values['roof'] = values['choise'];
        values['grant'] = 'true';
        values['tax'] = 'true';
        values['incvat'] = 'true';
        values['benefits'] = ['b1', 'b2', 'b3', 'b4', 'b5'];
        this.storage.setItem('roof', values['choise']);
        break;
    }

    this.storage.setItem('zip', values.zip);
    this.storage.setItem('term', 1);

    delete values['choise'];
    delete values['term'];

    this.onsubmit.emit(values);
  }

  onInputMouseEnter(event: MouseEvent) {
    const element = event.target as HTMLElement;
    this.choisePreview = (element && element.getAttribute('data-title')) || '';
  }

  onInputMouseLeave(event: MouseEvent) {
    this.choisePreview = '';
  }

  reset() {
    if (this.type === WidgetType.NORMAL) {
      return;
    }

    this.storage.removeItem('zip');
    this.storage.removeItem('roof');
    this.storage.removeItem('provider');
    this.rangeSelected = undefined;

    this.form.reset({
      zip: null,
      choise: null,
      value: this.product === 'fotowoltaika' ? 3500 : 60,
      term: !!this.storage.getItem('term')
    });

    this.setCurrentState();
    this.setFormValues();

    try {
      this.resetErrors();
      this.form.markAsPristine();
    } catch (e) {}
  }

  private setFormValues(): void {
    if (!this.form) {
      return;
    }
    this.onInputChanged();
    const fieldValue = this.form.get('value');
    const fieldChoise = this.form.get('choise');
    const valueFromParam =
      Number.isFinite(+getQueryParam('value')) && +getQueryParam('value') > 0
        ? +getQueryParam('value')
        : null;
    let choiseValue = null;
    switch (this.product) {
      case 'prad':
      case 'gaz':
        if (fieldValue.value > 500) {
          fieldValue.setValue(valueFromParam || 60);
        }
        if (this.storage.getItem('provider')) {
          choiseValue = this.storage.getItem('provider');
        }
        break;
      case 'fotowoltaika':
        if (fieldValue.value < 1001) {
          fieldValue.setValue(valueFromParam || 3500);
        }
        if (this.storage.getItem('roof')) {
          choiseValue = this.storage.getItem('roof');
        }
        break;
    }
    fieldChoise.reset(choiseValue);
    this.setCurrentState();
  }

  private setAssetExtension() {
    if (this.device.isiOS || this.device.isSafari) {
      this.renderer.addClass(this.element.nativeElement, 'jp2');
    } else {
      this.renderer.addClass(this.element.nativeElement, 'webp');
    }
  }

  private async scrollToError() {
    await sleep(300);
    const element = document.querySelector('sc-initial-form sc-error > p');
    if (!element) {
      return;
    }
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = element.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    animateScrollTo(elementPosition, {
      verticalOffset: -400,
      speed: 1400
    });
  }

  private setCurrentState() {
    if (!this.form) return;
    if (
      this.type !== WidgetType.ONE_PER_PAGE ||
      (this.type === WidgetType.ONE_PER_PAGE &&
        this.hiddenElements.length !== 0)
    ) {
      return;
    }

    const data = this.form.value;

    if (!data.zip) {
      this.currentView = FormSteps.ZIPCODE;
      return;
    }

    if (!data.choise) {
      this.currentView = FormSteps.CHOISE;
      return;
    }

    if (!data.value || !this.rangeSelected) {
      this.currentView = FormSteps.AMOUNT;
      this.rangeSelected = true;
      return;
    }

    if (!data.term) {
      this.currentView = FormSteps.TERM;
      return;
    }
  }
}
